import { supabase } from '@/lib/supabase';
import { appLogger } from '@/lib/logger';

export const SECURITY_EVENT_ACTIONS = [
  'PASSWORD_CHANGED',
  'INVITATION_ACCEPTED',
  'ROLE_CHANGED',
  'PROFILE_SENSITIVE_VIEWED',
  'PROFILE_SENSITIVE_EDITED',
  'PAYROLL_SENSITIVE_VIEWED',
  'PAYROLL_SENSITIVE_EDITED',
  'ACCOUNT_DELETION_REQUESTED',
  'ACCOUNT_DELETION_APPROVED',
  'ACCOUNT_DELETION_REJECTED',
  'ACCOUNT_DELETION_CANCELLED',
  'ACCOUNT_DATA_EXPORT_GENERATED',
] as const;

export type SecurityEventAction = (typeof SECURITY_EVENT_ACTIONS)[number];
type RecordableSecurityEventAction = Exclude<
  SecurityEventAction,
  | 'INVITATION_ACCEPTED'
  | 'ROLE_CHANGED'
  | 'PAYROLL_SENSITIVE_EDITED'
  | 'ACCOUNT_DELETION_REQUESTED'
  | 'ACCOUNT_DELETION_APPROVED'
  | 'ACCOUNT_DELETION_REJECTED'
  | 'ACCOUNT_DELETION_CANCELLED'
  | 'ACCOUNT_DATA_EXPORT_GENERATED'
>;

export const SECURITY_EVENT_LABELS: Record<SecurityEventAction, string> = {
  PASSWORD_CHANGED: 'Password changed',
  INVITATION_ACCEPTED: 'Invitation accepted',
  ROLE_CHANGED: 'Role changed',
  PROFILE_SENSITIVE_VIEWED: 'Sensitive profile viewed',
  PROFILE_SENSITIVE_EDITED: 'Sensitive profile edited',
  PAYROLL_SENSITIVE_VIEWED: 'Sensitive payroll viewed',
  PAYROLL_SENSITIVE_EDITED: 'Sensitive payroll edited',
  ACCOUNT_DELETION_REQUESTED: 'Account deletion requested',
  ACCOUNT_DELETION_APPROVED: 'Account deletion approved',
  ACCOUNT_DELETION_REJECTED: 'Account deletion request declined',
  ACCOUNT_DELETION_CANCELLED: 'Account deletion request cancelled',
  ACCOUNT_DATA_EXPORT_GENERATED: 'Account data export generated',
};

export function isSecurityEventAction(action: string): action is SecurityEventAction {
  return (SECURITY_EVENT_ACTIONS as readonly string[]).includes(action);
}

export async function recordSecurityEvent(input: {
  organizationId: string | null | undefined;
  action: RecordableSecurityEventAction;
  subjectUserId?: string | null;
}) {
  if (!input.organizationId) return false;

  const { data, error } = await supabase.rpc('record_security_event', {
    p_organization_id: input.organizationId,
    p_event_type: input.action,
    p_subject_user_id: input.subjectUserId ?? null,
  });

  if (error) {
    appLogger.warn('Security event could not be recorded.', error);
    return false;
  }

  return data;
}
